import React, { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { selectionStoreChangesService } from "../../services/selection-store-changes.service";
import { moduleSettings } from "../../stores/module-settings.store";
import { selectionStore } from "../../stores/selection.store";
import { GolEventListener } from "../calendar/calendar";
import moment from "moment";
import { Dropdown } from "../dropdown/dropdown";
import {
  stringArrayToDropdown,
  stringToDropdown,
} from "../dropdown/dropdown-transform";
import displayPeriodsBuilderService from "../../services/i-display-periods-builder.service";
import filtersService from "../../services/filters.service";
interface IStyle {
  forceAreaSelection: Boolean;
}

interface IFilters {
  alignPosition: FiltersPosition;
  shouldForceAreaSelection?: Boolean | false;
}

export enum FiltersPosition {
  start = "start",
  center = "center",
}

const FilterStyle = styled.div<IStyle>`
  display: flex;
  flex-direction: ${(props) =>
    props.forceAreaSelection ? "row-reverse" : "row"};
  width: 100%;
  justify-content: ${(props) => props.theme.alignPosition};
  align-items: start;
  text-align: center;
  margin-bottom: 30px;

  & > div {
    margin-left: 4px;
    margin-right: 4px;
  }

  #mui-component-select-outline {
    width: 254px;
    text-align: left;
  }

  .MuiOutlinedInput-input {
    padding-left: 16px;
  }

  @media screen and (max-width: 662px) {
     {
      flex-direction: ${(props) =>
        props.forceAreaSelection ? "column-reverse" : "column"};
      align-items: center;
      align-content: center;
    }

    & > div {
      margin-left: 0;
      margin-right: 0;
    }

    > * {
      &:first-child {
        margin-bottom: 8px;
      }
    }
  }

  @media screen and (max-width: 480px) {
    > * {
      flex: 0 0 100%;
      margin-bottom: 10px;
      margin-left: 0;
      margin-right: 0;
    }
  }
`;

const AreaSelector = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: start;
  gap: 2px;
`;

const TextDisplay = styled.div`
  max-width: 300px;
  color: ${(props) => props.theme.textColorDarken};
  font-size: 12px;
  text-align: left;
`;

const FiltersWrapper = (props: IFilters) => {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState(selectionStore.date);
  const [periodsState, setPeriodsState] = useState({
    loading: false,
    error: false,
  });
  const [isAreaSelectionDisabled, setIsAreaSelectionDisabled] = useState(
    filtersService.isAreaSelectionDisabled(null, periodsState)
  );
  const [showAreaCapacityWarning, setShowAreaCapacityWarning] = useState(false);

  useEffect(() => {
    selectionStoreChangesService.registerListener(
      "FiltersWrapper",
      [GolEventListener.CalendarDate],
      () => {
        setSelectedDate(selectionStore.date);
      }
    );
    return () => {
      selectionStoreChangesService.unregisterListener("FiltersWrapper");
    };
  }, []);

  useEffect(() => {
    if (selectedDate) {
      const isAreaSelectionDisabled = filtersService.isAreaSelectionDisabled(
        selectedDate,
        periodsState
      );
      const showWarning =
        filtersService.shouldShowAreaCapacityWarning(selectedDate);

      setIsAreaSelectionDisabled(isAreaSelectionDisabled);
      setShowAreaCapacityWarning(showWarning);
      selectionStore.isAreaSelectionDisabled = isAreaSelectionDisabled;

      // If forceAreaSelection is true and we're not showing capacity warning,
      // and current selection is "all", force user to make a selection
      if (
        moduleSettings.forceAreaSelection &&
        !showWarning &&
        selectionStore.area === "all"
      ) {
        // Reset to null to force user selection with placeholder
        selectionStore.area = null;
        selectionStoreChangesService.dispatch(GolEventListener.Area);
      }
    }
  }, [selectedDate, periodsState]);

  useEffect(() => {
    const sub = displayPeriodsBuilderService.periodsState$.subscribe(
      (state) => {
        setPeriodsState(state);
      }
    );
    return () => sub.unsubscribe();
  }, []);

  const periodChanged = (value: any) => {
    if (selectionStore.period !== value) {
      selectionStore.period = value;
      selectionStoreChangesService.dispatch(GolEventListener.Period);
    }
  };

  const areaChanged = (value: any) => {
    if (selectionStore.area !== value) {
      selectionStore.area = value;
      selectionStoreChangesService.dispatch(GolEventListener.Area);
    }
  };

  const periodsOptions = stringArrayToDropdown(
    moduleSettings.periods,
    selectionStore.period,
    "periods."
  );
  const areasOptions = filtersService.areaDropdownOptions(
    moduleSettings.areas,
    selectionStore.area
  );
  const forceAreaSelection = moduleSettings.forceAreaSelection;
  const areaPlaceholder =
    forceAreaSelection && !showAreaCapacityWarning ? "placeholder" : "all";
  const hideServiceSelection = moduleSettings.hideServiceSelection;

  return (
    <FilterStyle
      data-test="filters"
      theme={props}
      forceAreaSelection={forceAreaSelection}
    >
      {!hideServiceSelection &&
        periodsOptions.length > 1 &&
        !props.shouldForceAreaSelection && (
          <Dropdown
            dataTest="dropdownPeriods"
            options={periodsOptions}
            placeholderOption={stringToDropdown("all", null, "periods.")}
            placeholderSelectable={true}
            onItemClick={periodChanged}
          />
        )}
      {areasOptions ? (
        <AreaSelector>
          <Dropdown
            requiredWithPlaceholder={
              forceAreaSelection && !showAreaCapacityWarning
            }
            dataTest="dropdownAreas"
            options={areasOptions}
            placeholderOption={stringToDropdown(
              areaPlaceholder,
              null,
              "rooms."
            )}
            placeholderSelectable={
              !forceAreaSelection || showAreaCapacityWarning
            }
            onItemClick={areaChanged}
            disabled={periodsState.loading}
          />
          {showAreaCapacityWarning && (
            <TextDisplay>
              <span>
                {"* " + t("instabook.errors.requested_area_but_capa")}
              </span>
            </TextDisplay>
          )}
        </AreaSelector>
      ) : (
        ""
      )}
    </FilterStyle>
  );
};

export const Filters = memo(FiltersWrapper);
